import { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { createSlice, createSelector, current } from '@reduxjs/toolkit';

import {
  transformInitialEvaluationValuesFood,
  transformInitialEvaluationValuesNonFood,
} from 'pages/new-evaluation-form/utils/mappingValueEvaluationForm';

import {
  isAllergensNonFoodForm,
  isFoodForm,
  isNonFoodForm,
} from 'pages/new-evaluation-form/utils/checkTypeEvaluationForm';

import {
  getChangedEvaluationForm,
  getChangedHeaderNamePhotoLabelEvalForm,
  getUpdatedFieldInfo,
  getValueOfFieldEvaluationForm,
} from 'pages/new-evaluation-form/utils/getChangedEvaluationForm';

import { mappingReadOnlyAllergens } from 'pages/new-evaluation-form/utils/validateSubmitEvalForm';

const initialState = {
  dataTable: {
    food: {},
    'non-food': {},
    readOnlyAllergens: {},
  },
  initialDataTable: {
    food: {},
    'non-food': {},
    readOnlyAllergens: {},
  },
  statusChangeFormValues: 'idle',
  nonFoodSpecificationAllergens: [],
};

export const useGetEvalFormType = (type) => {
  const evaluationFormData = useSelector(selectEvaluationForm) ?? {};

  return useMemo(
    () => evaluationFormData?.dataTable?.[type] ?? {},
    [type, evaluationFormData]
  );
};

export const useGetEvalFormTableType = ({ type, nameTable }) => {
  const evaluationFormType = useSelector(
    selectEvaluationFormByType(type, nameTable)
  );

  return evaluationFormType;
};

export const useGetInitialEvalFormTableType = ({ type, nameTable }) => {
  const evaluationFormType = useSelector(
    selectInitialEvaluationFormByType(type, nameTable)
  );

  return evaluationFormType;
};

export const useGetNonFoodTable = (nameTable) => {
  const evaluationFormData = useSelector(selectEvaluationForm) ?? {};

  return useMemo(
    () => evaluationFormData?.nonFoodTable?.[nameTable] ?? [],
    [nameTable, evaluationFormData]
  );
};

export const useGetReadOnlyAllergensNonFood = () => {
  return useSelector(selectReadOnlyAllergens()) ?? {};
};

export const useGetQaEvaluationForm = () => {
  return useSelector(selectEvaluationForm) ?? {};
};

export const useGetSpecificProductImagery = ({
  type,
  nameTable,
  propertyName,
}) => {
  const productImagery = useGetEvalFormTableType({ type, nameTable });

  return useMemo(
    () => productImagery[propertyName] ?? [],
    [productImagery, propertyName]
  );
};

export const useGetProductImagery = (type) => {
  return useSelector(selectProductImageryFood(type));
};

export const useGetNonFoodSpecificationAllergens = () => {
  return useSelector(selectNonFoodSpecificationAllergens());
};

export const evaluationFormSlice = createSlice({
  name: 'evaluationForm',
  initialState,
  reducers: {
    initialEvaluationForm: (state, action) => {
      const { type, values } = action?.payload ?? {};
      const qaEvaluationFormDetail = values?.qaEvaluationFormDetail ?? {};
      const qaEvaluationFormAssetDetails =
        values?.qaEvaluationFormAssetDetails ?? [];

      if (isFoodForm(type)) {
        const mappingInitialValues = transformInitialEvaluationValuesFood(
          qaEvaluationFormDetail,
          qaEvaluationFormAssetDetails
        );

        state.dataTable[type?.toLowerCase()] = mappingInitialValues;
        state.initialDataTable[type?.toLowerCase()] = mappingInitialValues;
        state.statusChangeFormValues = 'idle';
      } else if (isNonFoodForm(type)) {
        const mappingInitialValues = transformInitialEvaluationValuesNonFood(
          qaEvaluationFormDetail,
          qaEvaluationFormAssetDetails
        );

        state.dataTable['non-food'] = mappingInitialValues;
        state.initialDataTable['non-food'] = mappingInitialValues;
        state.initialDataTable.readOnlyAllergens =
          mappingReadOnlyAllergens(values?.readOnlyAllergens) ?? {};
        state.statusChangeFormValues = 'idle';
      }
    },

    updateEvaluationForm: (state, action) => {
      const {
        type,
        nameTable,
        photoLabel = false,
        indexOfFields,
        changedValue,
      } = action.payload;

      if (photoLabel) {
        const beforeTableData = current(state.dataTable[type][nameTable][0]);
        const [propertyName, index, changedProperty] = indexOfFields;

        const newTableSource = getChangedHeaderNamePhotoLabelEvalForm({
          tableDataSource: beforeTableData,
          name: indexOfFields,
          changedValue,
        });

        const beforeValue =
          beforeTableData?.[propertyName]?.[index]?.[changedProperty];
        const afterValue =
          newTableSource?.[0]?.[propertyName]?.[index]?.[changedProperty];

        if (beforeValue !== afterValue) {
          const [propertyName, index, changedProperty] = indexOfFields;

          state.dataTable[type][nameTable][0][propertyName][index][
            changedProperty
          ] = changedValue;
          state.statusChangeFormValues = 'changed';
        }
      } else {
        const readOnlyAllergens = current(
          state.initialDataTable.readOnlyAllergens
        );
        const beforeTableData = current(state.dataTable[type][nameTable]);
        const beforeValue =
          getValueOfFieldEvaluationForm({
            tableDataSource: beforeTableData,
            indexOfFields,
          }) ?? '';

        const newTableSource = getChangedEvaluationForm({
          tableDataSource: beforeTableData,
          name: indexOfFields,
          changedValue,
        });

        const afterValue =
          getValueOfFieldEvaluationForm({
            tableDataSource: newTableSource,
            indexOfFields,
          }) ?? '';

        if (beforeValue !== afterValue) {
          const { propertyName, changedProperty } =
            getUpdatedFieldInfo(indexOfFields);

          const foundIndex = beforeTableData.findIndex(
            (item) => item.property.name === propertyName
          );

          state.dataTable[type][nameTable][foundIndex][`${changedProperty}`] =
            changedValue;
          state.statusChangeFormValues = 'changed';

          if (
            isAllergensNonFoodForm(type, nameTable) &&
            changedProperty === 'headerName'
          ) {
            state.dataTable[type][nameTable][foundIndex]['specification'] =
              readOnlyAllergens[changedValue?.toLowerCase()] ?? '';
          }
        }
      }
    },

    uploadHeaderImage: (state, action) => {
      const { type, imageObject, propertyName } = action.payload;
      state.dataTable[type].headerImage[0][propertyName] = imageObject;
      state.statusChangeFormValues = 'changed';
    },

    uploadProductImagery: (state, action) => {
      const { type, imageObject, nameTable, propertyName } = action.payload;

      const [parentPropertyName, childPropertyName] = propertyName;

      state.dataTable[type][nameTable][0][parentPropertyName][
        childPropertyName
      ]['digitalAssetId'] = imageObject;

      state.statusChangeFormValues = 'changed';
    },

    updateStatusChangeFormValues: (state, action) => {
      const { status } = action.payload;

      state.statusChangeFormValues = status;
    },

    updateNonFoodSpecificationAllergens: (state, action) => {
      const { index, value } = action.payload;
      state.nonFoodSpecificationAllergens[index] = value;
    },
  },
});

export const selectEvaluationForm = createSelector(
  (state) => state,
  (state) => state?.evaluationForm
);

export const selectEvaluationFormByType = (type, nameTable) =>
  createSelector(
    selectEvaluationForm,
    (state) => state?.dataTable?.[type]?.[nameTable] ?? []
  );

export const selectInitialEvaluationFormByType = (type, nameTable) =>
  createSelector(
    selectEvaluationForm,
    (state) => state?.initialDataTable?.[type]?.[nameTable] ?? []
  );

export const selectReadOnlyAllergens = () =>
  createSelector(
    selectEvaluationForm,
    (state) => state?.initialDataTable?.readOnlyAllergens ?? {}
  );

export const selectProductImageryFood = (type) =>
  createSelector(
    selectEvaluationForm,
    (state) => state?.dataTable?.[type]?.productImagery
  );

export const selectProductImageryNonFood = () =>
  createSelector(
    selectEvaluationForm,
    (state) => state?.dataTable?.nonfood?.productImagery
  );

export const selectStatusChangeFormValues = () => {
  return createSelector(
    selectEvaluationForm,
    (state) => state?.statusChangeFormValues
  );
};

export const selectNonFoodSpecificationAllergens = () => {
  return createSelector(
    selectEvaluationForm,
    (state) => state?.nonFoodSpecificationAllergens
  );
};

export const {
  updateEvaluationForm,
  uploadHeaderImage,
  uploadProductImagery,
  initialEvaluationForm,
  updateStatusChangeFormValues,
  updateNonFoodSpecificationAllergens,
} = evaluationFormSlice.actions;

export default evaluationFormSlice.reducer;
